import { isString } from 'util/strings'

const getLocalStorageStats = () => {
  const localStorageStats = {}
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      const value = localStorage.getItem(key)
      localStorageStats[key] = value.length
    }
  } catch (e) {
    localStorageStats.failed = 1
  }
  return localStorageStats
}

const reportToBugsnag = (e, type) => {
  if (!bugsnagClient) return

  let errorDetails = null

  if (e.fromPromise) errorDetails = e

  bugsnagClient.notify(e, {
    severity: 'error',
    metaData: {
      type,
      global: true,
      fatal: e.fatal,
      localstorage_stats: getLocalStorageStats(),
      error_details: errorDetails,
    },
  })
}

const displayErrorMessage = (e, type = 'Non-auth') => {
  if (!e) return
  // Don't double report API errors
  if (e.doNotReport) return
  if (e.message && e.message.match('Network request failed')) return

  reportToBugsnag(e, type)

  if (e.fatal) {
    const error = document.getElementById('error')
    const app = document.getElementById('app')

    app.style.display = 'none'
    error.style.display = 'block'
  }
}

const displayBetaErrorMessage = () => {
  const error = document.getElementById('beta-error')
  const appInner = document.getElementById('app-inner')

  error.style.display = 'block'
  appInner.style.display = 'none'
}

const errorCodes = error => {
  const errors = error ? error.errors : undefined
  const statuses = []
  if (errors) {
    errors.forEach(e => {
      if (e) {
        try {
          const message = JSON.parse(e.message)
          statuses.push(message.status)
        } catch (ex) {
          // jank. Assumes its not a json error.
          statuses.push(e.status)
        }
      }
    })
  }

  return statuses
}

const isUnauthorized = error => {
  return errorCodes(error).indexOf(401) !== -1
}

const isBetaError = error => {
  return errorCodes(error).indexOf(542) !== -1
}

const handleError = error => {
  // detect if the error is something you know how to handle
  if (isBetaError(error)) {
    return displayBetaErrorMessage()
  }

  if (isUnauthorized(error)) {
    reportToBugsnag(error, 'Logout-triggering authorization failure')
  } else {
    displayErrorMessage(error)
  }

  // return true to short-circuit default error behavior
  return true
}

function isServiceWorkerRegistrationError(err = {}) {
  const { code, name, message } = err.reason || {}
  // Jank. The offline-plugin's `install()` will reject, but not allow you to
  // catch it. Go figure.
  return (
    code === 20 &&
    name === 'AbortError' &&
    message ===
      'Failed to register a ServiceWorker: ServiceWorker cannot be started'
  )
}

function isServiceWorkerSecurityError(err = {}) {
  const { code, name, message } = err.reason || {}
  return (
    code === 18 &&
    name === 'SecurityError' &&
    message ===
      'Failed to register a ServiceWorker: The script resource is behind a redirect, which is disallowed.'
  )
}

// We want to show the error page on unhandled promise rejections
window.addEventListener('unhandledrejection', err => {
  // Jank. The offline-plugin's `install()` will reject, but not allow you to
  // catch it. Go figure.
  if (isServiceWorkerRegistrationError(err)) {
    // eslint-disable-next-line no-console
    console.error(
      'Global unhandled rejection: SW registration error - skipping'
    )
    return false
  }

  if (isServiceWorkerSecurityError(err)) {
    // eslint-disable-next-line no-console
    console.error('Global unhandled rejection: SW security error - skipping')
    return false
  }
  let { reason } = err
  if (isString(reason)) {
    reason = {
      name: reason,
      message: reason,
    }
  }
  reason.fromPromise = true
  reason.errorType = typeof err
  reason.objectKeys = Object.keys(err)

  handleError(reason)
  return false
})

window.testUnhandledRejectionBroken = () => {
  // eslint-disable-next-line prefer-promise-reject-errors
  Promise.reject({})
}

window.testUnhandledRejection = () => {
  // eslint-disable-next-line prefer-promise-reject-errors
  Promise.reject('test error')
}

window.testRegularError = () => {
  throw 'test error' // eslint-disable-line no-throw-literal
}

window.testCustomError = () => {
  const error = new Error('custom')
  error.doNotReport = true
  throw error
}

window.testUnhandledDoNotReportRejection = () => {
  const error = new Error('custom')
  error.doNotReport = true
  Promise.reject(error)
}

window.onerror = (msg, url, lineNo, columnNo, error) => {
  // If instead of a descriptive error message we get a general
  // 'Script error.', it means error occurred in a script with
  // different origin (e.g. in an iframe created by an editor),
  // i.e. it's not a problem with our code.
  //
  // In such cases it's better to assume that that code is able to
  // continue to work in presence of errors rather than kill the
  // whole app — we don't have enough information to effectively
  // debug the problem anyway.
  //
  // More info:
  //   https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror#Notes
  if (msg === 'Script error.') return

  handleError(error)
}

if (!('toJSON' in Error.prototype))
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(Error.prototype, 'toJSON', {
    value() {
      const alt = {}

      Object.getOwnPropertyNames(this).forEach(key => {
        alt[key] = this[key]
      }, this)

      return alt
    },
    configurable: true,
    writable: true,
  })

export function GraphQlValidationError(fields) {
  Object.assign(this, fields)
  this.fields = fields
  this.message = `Field validation failed for [${Object.keys(fields)
    .filter(k => k !== '_mapped')
    .join(',')}]`
}
GraphQlValidationError.prototype = Error.prototype
